/**
 * Web-wide API configuration.
 *
 * SERVER_URL is injected at build time via the Vite `define` in
 * apps/web/vite.config.ts (which resolves VITE_SERVER_ORIGIN).
 * vite.config.ts already emits a console.warn at build/dev-server
 * startup when VITE_SERVER_ORIGIN is missing — that is the "warning
 * in dev" apps/web/CLAUDE.md refers to.
 *
 * The module-level fallback below is a belt-and-suspenders guard for
 * environments that bypass the Vite build (e.g. a bare `tsc` run or
 * a custom bundler). It also emits a dev-only warn so the silent
 * production-origin default can't sneak past.
 */

declare const __SERVER_URL__: string | undefined;

const injected = typeof __SERVER_URL__ !== "undefined" ? __SERVER_URL__ : null;
const SERVER_URL: string = injected ?? "https://sync.reflecto.dev";

if (injected === null && import.meta.env.DEV) {
  console.warn(
    "[reflecto-web] VITE_SERVER_ORIGIN missing at runtime — using production default. " +
      "Ensure vite.config.ts is applying the `define` for __SERVER_URL__."
  );
}

/** Use this for all API calls. `SERVER_URL` above is private and stays that way. */
export const API_BASE = `${SERVER_URL}/v1`;

/**
 * The bare origin, WITHOUT the `/v1` prefix — for `@reflecto/client-core/api` only.
 *
 * The rule above still stands for everything else: hand-building `/v1` paths at a call site is
 * how the version prefix drifts. The shared client is the one legitimate exception because it
 * owns its own paths (`/v1/sync`, `/v1/messages/…`), so handing it `API_BASE` would produce
 * `/v1/v1/sync`. Exported rather than derived by stripping a suffix off `API_BASE`, which
 * would be a regex quietly depending on a format defined one line above it.
 */
export const SERVER_ORIGIN = SERVER_URL;
